import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase/config";
import {
  resolveUserModules,
  userHasModule,
} from "../lib/cmsAccess";
import { defaultCmsRoles, mergeCmsRoleCatalog, roleIsStaffOtp } from "../lib/cmsRoles";

const CmsAuthContext = createContext(null);

/** Cierre por inactividad: owner/admin/editor (secciones sensibles). */
export const CMS_STAFF_IDLE_TIMEOUT_MS = 10 * 60 * 1000;

/** Roles operativos (vendedor, calidad, …): jornada laboral sin re-login constante. */
export const CMS_OPERATIONAL_IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;

/** @deprecated usar idleTimeoutForCmsRole */
export const CMS_IDLE_TIMEOUT_MS = CMS_STAFF_IDLE_TIMEOUT_MS;

export function idleTimeoutForCmsRole(role, catalog) {
  if (roleIsStaffOtp(catalog, role)) {
    return CMS_STAFF_IDLE_TIMEOUT_MS;
  }
  return CMS_OPERATIONAL_IDLE_TIMEOUT_MS;
}

/** Eventos que demuestran uso activo del panel (captura en document). */
const IDLE_EVENTS = [
  "mousemove",
  "mousedown",
  "pointerdown",
  "keydown",
  "keyup",
  "scroll",
  "touchstart",
  "touchmove",
  "wheel",
  "click",
  "input",
  "change",
  "focusin",
];

const OTP_REQUESTED_KEY = (uid) => `cmsOtpRequested:${uid}`;

export function markCmsOtpRequested(uid) {
  if (!uid) return;
  try {
    sessionStorage.setItem(OTP_REQUESTED_KEY(uid), String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function wasCmsOtpRequested(uid) {
  if (!uid) return false;
  try {
    return Boolean(sessionStorage.getItem(OTP_REQUESTED_KEY(uid)));
  } catch {
    return false;
  }
}

export function clearCmsOtpRequested(uid) {
  if (!uid) return;
  try {
    sessionStorage.removeItem(OTP_REQUESTED_KEY(uid));
  } catch {
    /* ignore */
  }
}

function mapAuthError(err) {
  const code = err?.code || "";
  if (
    code === "auth/invalid-credential" ||
    code === "auth/wrong-password" ||
    code === "auth/user-not-found"
  ) {
    return "Correo o contraseña incorrectos. Si es el primer acceso, ejecute: npm run cms:bootstrap";
  }
  if (code === "auth/too-many-requests") {
    return "Demasiados intentos. Espere unos minutos.";
  }
  if (code === "auth/network-request-failed") {
    return "Error de red. Verifique su conexión.";
  }
  return err?.message || "No se pudo iniciar sesión.";
}

/** Extrae el mensaje útil de callables (HttpsError). */
function mapCallableError(err, fallback = "No se pudo completar la operación.") {
  const code = String(err?.code || "");
  const raw = String(err?.message || "").trim();
  // A veces el SDK antepone "FirebaseError: " o el código HTTP.
  const cleaned = raw
    .replace(/^FirebaseError:\s*/i, "")
    .replace(/^functions\/[a-z-]+\s*/i, "")
    .trim();

  if (code === "functions/permission-denied") {
    if (/incorrecto/i.test(cleaned)) {
      return cleaned || "Código incorrecto. Use el del correo más reciente.";
    }
    if (/Website|sistemas|autorizado|inactivo|insuficiente/i.test(cleaned)) {
      return cleaned;
    }
    return cleaned || "Sin permiso para esta operación.";
  }
  if (code === "functions/failed-precondition") {
    return cleaned || "Solicite un código OTP primero.";
  }
  if (code === "functions/deadline-exceeded") {
    return cleaned || "El código ha expirado. Pulse Reenviar código.";
  }
  if (code === "functions/resource-exhausted") {
    return cleaned || "Demasiados intentos o espere ~60 s para reenviar.";
  }
  if (code === "functions/invalid-argument") {
    return cleaned || "Código OTP inválido.";
  }
  if (code === "functions/unauthenticated") {
    return "Sesión expirada. Vuelva a iniciar sesión.";
  }
  return cleaned || raw || fallback;
}

export function CmsAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [roleCatalog, setRoleCatalog] = useState(() => defaultCmsRoles());

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "site", "cmsRoles"),
      (snap) => {
        setRoleCatalog(mergeCmsRoleCatalog(snap.data()?.roles));
      },
      () => {
        setRoleCatalog(defaultCmsRoles());
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    let unsubProfile = null;
    let unsubSession = null;
    let cancelled = false;

    (async () => {
      try {
        await auth.authStateReady();
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
        }
      } catch {
        try {
          await signOut(auth);
        } catch {
          /* ignore */
        }
      }
    })();

    const unsubAuth = onAuthStateChanged(auth, async (nextUser) => {
      if (cancelled) return;
      if (unsubProfile) unsubProfile();
      if (unsubSession) unsubSession();
      unsubProfile = null;
      unsubSession = null;

      setUser(nextUser);
      setProfile(null);
      setSession(null);

      if (!nextUser) {
        setLoading(false);
        return;
      }

      setLoading(true);
      unsubProfile = onSnapshot(
        doc(db, "users", nextUser.uid),
        (snap) => {
          setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
          setLoading(false);
        },
        async () => {
          setProfile(null);
          setLoading(false);
          try {
            await signOut(auth);
          } catch {
            /* ignore */
          }
        }
      );

      unsubSession = onSnapshot(
        doc(db, "cmsSessions", nextUser.uid),
        (snap) => {
          setSession(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        },
        () => setSession(null)
      );
    });

    return () => {
      cancelled = true;
      unsubAuth();
      if (unsubProfile) unsubProfile();
      if (unsubSession) unsubSession();
    };
  }, []);

  const isCmsEditor = useMemo(() => {
    return (
      !!profile &&
      ["owner", "editor"].includes(profile.role) &&
      profile.status === "active"
    );
  }, [profile]);

  const isCmsStaff = useMemo(() => {
    return !!profile && profile.status === "active";
  }, [profile]);

  const isVendedor = useMemo(
    () => !!profile && profile.role === "vendedor" && profile.status === "active",
    [profile]
  );

  const isCalidad = useMemo(
    () => !!profile && profile.role === "calidad" && profile.status === "active",
    [profile]
  );

  const accessibleModules = useMemo(
    () => resolveUserModules(profile, roleCatalog),
    [profile, roleCatalog]
  );

  const hasModule = useCallback(
    (moduleId) => userHasModule(profile, moduleId, roleCatalog),
    [profile, roleCatalog]
  );

  const otpVerified = useMemo(() => {
    if (!session?.verifiedUntil) return false;
    const until = session.verifiedUntil.toDate
      ? session.verifiedUntil.toDate()
      : new Date(session.verifiedUntil);
    return until.getTime() > Date.now();
  }, [session]);

  const canAccessCms = isCmsStaff && otpVerified;

  const mustSetPassword = profile?.mustSetPassword === true;

  const login = useCallback(async (email, password) => {
    setAuthError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const profileSnap = await getDoc(doc(db, "users", cred.user.uid));
      if (!profileSnap.exists()) {
        await signOut(auth);
        throw new Error(
          "Usuario no autorizado para el CMS. Ejecute npm run cms:bootstrap o asigne rol."
        );
      }
      const data = profileSnap.data();
      if (data.status !== "active") {
        await signOut(auth);
        throw new Error("Cuenta CMS inactiva o sin permisos.");
      }
      const systems = Array.isArray(data.systems) ? data.systems : null;
      const hasWebsite =
        data.role === "owner" ||
        systems == null ||
        systems.includes("website");
      if (!hasWebsite) {
        await signOut(auth);
        throw new Error(
          "Esta cuenta no tiene acceso al CMS Website. Contacte al administrador del sistema."
        );
      }
      return cred.user;
    } catch (err) {
      const message = mapAuthError(err);
      setAuthError(message);
      const wrapped = new Error(message);
      wrapped.code = err?.code;
      throw wrapped;
    }
  }, []);

  const requestOtp = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, "requestCmsOtp");
      const result = await fn({});
      return result.data;
    } catch (err) {
      throw new Error(mapCallableError(err, "No se pudo enviar el código."));
    }
  }, []);

  const verifyOtp = useCallback(async (code) => {
    try {
      const fn = httpsCallable(functions, "verifyCmsOtp");
      const result = await fn({ code: String(code || "").replace(/\D/g, "").slice(0, 6) });
      return result.data;
    } catch (err) {
      throw new Error(mapCallableError(err, "Código incorrecto."));
    }
  }, []);

  const clearOtpSession = useCallback(async (wipeOtp = true) => {
    try {
      const fn = httpsCallable(functions, "clearCmsSession");
      await fn({ wipeOtp });
    } catch {
      // ignore
    }
  }, []);

  const logout = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    await clearOtpSession(true);
    clearCmsOtpRequested(uid);
    await signOut(auth);
  }, [clearOtpSession]);

  // Inactividad staff: 10 min sin teclado/ratón en el sitio, o 10 min con la pestaña/ventana fuera de foco.
  // Volver a enfocar no reinicia el reloj; solo comprueba si ya venció.
  useEffect(() => {
    if (!otpVerified) return undefined;

    const idleMs = idleTimeoutForCmsRole(profile?.role, roleCatalog);
    let lastActivity = Date.now();
    let closing = false;
    let intervalId = null;

    const markActivity = () => {
      lastActivity = Date.now();
    };

    const maybeLogout = async () => {
      if (closing) return;
      if (Date.now() - lastActivity < idleMs) return;
      closing = true;
      try {
        sessionStorage.setItem("cmsIdleLogout", "1");
        const mins = Math.round(idleMs / 60_000);
        sessionStorage.setItem(
          "cmsIdleLogoutLabel",
          mins >= 60 ? `${Math.round(mins / 60)} h` : `${mins} min`
        );
      } catch {
        /* ignore */
      }
      const uid = auth.currentUser?.uid;
      await clearOtpSession(false);
      clearCmsOtpRequested(uid);
      await signOut(auth);
    };

    intervalId = window.setInterval(() => {
      void maybeLogout();
    }, 15_000);

    const onVisible = () => {
      if (document.visibilityState === "visible") void maybeLogout();
    };
    const onWindowFocus = () => {
      void maybeLogout();
    };

    for (const ev of IDLE_EVENTS) {
      document.addEventListener(ev, markActivity, { capture: true, passive: true });
    }
    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      for (const ev of IDLE_EVENTS) {
        document.removeEventListener(ev, markActivity, { capture: true });
      }
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [otpVerified, profile?.role, roleCatalog, clearOtpSession]);

  const idleTimeoutSec = idleTimeoutForCmsRole(profile?.role, roleCatalog) / 1000;

  const value = {
    user,
    profile,
    session,
    loading,
    authError,
    isCmsEditor,
    isCmsStaff,
    isVendedor,
    isCalidad,
    accessibleModules,
    hasModule,
    roleCatalog,
    otpVerified,
    canAccessCms,
    mustSetPassword,
    idleTimeoutSec,
    login,
    requestOtp,
    verifyOtp,
    logout,
  };

  return (
    <CmsAuthContext.Provider value={value}>{children}</CmsAuthContext.Provider>
  );
}

export function useCmsAuth() {
  const ctx = useContext(CmsAuthContext);
  if (!ctx) {
    throw new Error("useCmsAuth debe usarse dentro de CmsAuthProvider");
  }
  return ctx;
}
